import React from 'react'
import { Link } from 'react-router-dom'
import { Shield, Loader2 } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Alert,
  AlertDescription,
} from '@atrvd/saas-ui'
import { useMFA } from '@/hooks/useMFA'

/**
 * Props for the RequireMFA gate component.
 *
 * @property children - Content to render when the user has MFA enabled.
 */
interface RequireMFAProps {
  children: React.ReactNode
}

/**
 * Gate component that blocks access to its children until the current user
 * has at least one verified TOTP factor enrolled.
 *
 * Renders three states:
 * - Loading: spinner while the factors query is in flight
 * - Blocked: card with an explanation and a link to security settings when hasMFA is false
 * - Allowed: renders children as-is when hasMFA is true
 *
 * Usage:
 * ```tsx
 * <RequireMFA>
 *   <SensitivePage />
 * </RequireMFA>
 * ```
 */
export function RequireMFA({ children }: RequireMFAProps) {
  const { factors, hasMFA } = useMFA()

  // Show a centred spinner while the factors query is in flight
  if (factors.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Block access and prompt the user to enable MFA when no verified factor exists
  if (!hasMFA) {
    return (
      <div className="max-w-lg mx-auto mt-12">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Two-Factor Authentication Required
            </CardTitle>
            <CardDescription>
              This section requires two-factor authentication to be enabled on your account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/*
             * Explain why MFA is required in this area of the app so the user
             * understands the reason before being redirected to settings.
             */}
            <Alert>
              <AlertDescription>
                Access to banking operations and financial data requires an additional layer of
                security. Please enable two-factor authentication on your account to protect
                sensitive financial information.
              </AlertDescription>
            </Alert>

            <Button asChild>
              <Link to="/settings/profile">Go to Security Settings</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // MFA is enabled — render children normally
  return <>{children}</>
}
